<script setup lang="ts">
/**
 * AdminTopbar — верхняя панель админ-лейаута (admin-kit): поиск, смена темы,
 * уведомления, пользователь, тогглы сайдбара/aside. Цвета только из токенов.
 */
import { useAuthStore, useAdminSessionManager } from '~/features/auth'
import { canAccessAdminPath } from '~/shared/config'
// Явный импорт, а не автоимпорт: барные index.ts исключены из imports.dirs
import { useDarkMode } from '~/shared/lib'

defineProps<{ asideAvailable?: boolean }>()
const emit = defineEmits<{ (e: 'toggleMobile'): void, (e: 'toggleSidebar'): void, (e: 'toggleAsideMobile'): void }>()

defineOptions({ name: 'AdminTopbar' })

const auth = useAuthStore()

// Доступность закрытых для роли разделов (manager не видит уведомления/настройки)
const adminSession = useAdminSessionManager()
const canNotifications = computed(() => canAccessAdminPath(adminSession.getAdminRole(), '/administration/notifications'))
const canSettings = computed(() => canAccessAdminPath(adminSession.getAdminRole(), '/administration/settings'))
const { toggle: toggleAside } = useAdminAside()

// Тема переключается тем же композаблом, что и на витрине: круговая заливка
// от точки клика и морфинг иконки — см. shared/lib/composables/useDarkMode.ts
const { isDark, toggleTheme } = useDarkMode()

// Поиск → переход к списку товаров с query
const search = ref('')
function submitSearch() {
  const q = search.value.trim()
  navigateTo({ path: '/administration/products', query: q ? { search: q } : {} })
}

// Уведомления (заглушка — подключить к реальному стору позже)
const notifOpen = ref(false)
const notifRef = ref<HTMLElement | null>(null)
const notifications = [
  { id: 1, title: 'Новый заказ #1284', time: '2 мин назад', color: 'success' as const },
  { id: 2, title: 'Товар скоро закончится', time: '15 мин назад', color: 'warning' as const },
  { id: 3, title: 'Сбой синхронизации', time: '1 ч назад', color: 'danger' as const },
]
const unread = computed(() => notifications.length)
onClickOutside(notifRef, () => { notifOpen.value = false })

// Меню пользователя
const userOpen = ref(false)
const userRef = ref<HTMLElement | null>(null)
onClickOutside(userRef, () => { userOpen.value = false })
function logout() {
  ;(auth as unknown as { adminlogout: () => void }).adminlogout()
}

const iconBtn = 'relative inline-flex items-center justify-center w-9 h-9 rounded-hui-md text-hui-foreground/70 hover:bg-hui-default-100 hover:text-hui-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-hui-focus'
</script>

<template>
  <header class="flex items-center gap-2 sm:gap-3 h-14 shrink-0 px-1 sm:px-2">
    <!-- Тоггл левого сайдбара перед поиском: мобайл — drawer, десктоп — свернуть/развернуть -->
    <button type="button" :class="[iconBtn, 'lg:hidden']" aria-label="Меню" @click="emit('toggleMobile')">
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <line x1="9" y1="4" x2="9" y2="20" stroke-linecap="round" />
      </svg>
    </button>
    <button type="button" :class="[iconBtn, 'hidden lg:inline-flex']" aria-label="Свернуть меню" @click="emit('toggleSidebar')">
      <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <line x1="9" y1="4" x2="9" y2="20" stroke-linecap="round" />
      </svg>
    </button>

    <!-- Поиск -->
    <form class="flex-1 max-w-md" @submit.prevent="submitSearch">
      <UiKitInput v-model="search" size="sm" variant="flat" placeholder="Поиск товаров…" clearable>
        <template #leading>
          <Icon name="heroicons:magnifying-glass" class="w-4 h-4" />
        </template>
      </UiKitInput>
    </form>

    <div class="ml-auto flex items-center gap-1 sm:gap-2">
      <!-- Тема -->
      <button type="button" :class="iconBtn" :aria-label="isDark ? 'Светлая тема' : 'Тёмная тема'" @click="toggleTheme">
        <!-- `:key` — анимация line-md играет один раз при монтировании, без
             пересоздания узла переход «солнце → луна» не запустится.
             `mode="svg"` — в дефолтном CSS-режиме иконка становится маской,
             а SVG внутри маски браузер не анимирует вовсе. -->
        <Icon
          :key="isDark ? 'moon' : 'sun'"
          mode="svg"
          :name="isDark ? 'line-md:sunny-outline-to-moon-transition' : 'line-md:moon-to-sunny-outline-transition'"
          class="w-5 h-5"
        />
      </button>

      <!-- Уведомления -->
      <div v-if="canNotifications" ref="notifRef" class="relative">
        <button type="button" :class="iconBtn" aria-label="Уведомления" @click="notifOpen = !notifOpen">
          <Icon name="heroicons:bell" class="w-5 h-5" />
          <span v-if="unread > 0" class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-hui-danger ring-2 ring-hui-content1" />
        </button>
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          leave-active-class="transition duration-100 ease-in"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div
            v-if="notifOpen"
            class="absolute right-0 mt-2 w-72 rounded-hui-md border border-hui-divider bg-hui-content1 shadow-lg overflow-hidden z-50"
          >
            <div class="px-3 py-2 border-b border-hui-divider flex items-center justify-between">
              <span class="text-sm font-medium text-hui-foreground">Уведомления</span>
              <UiKitChip size="sm" color="danger" variant="flat">{{ unread }}</UiKitChip>
            </div>
            <ul class="max-h-72 overflow-auto">
              <li
                v-for="n in notifications"
                :key="n.id"
                class="flex items-start gap-2.5 px-3 py-2.5 hover:bg-hui-default-100 transition-colors"
              >
                <span class="mt-1.5 w-2 h-2 rounded-full shrink-0" :class="`bg-hui-${n.color}`" />
                <div class="min-w-0">
                  <p class="text-sm text-hui-foreground truncate">{{ n.title }}</p>
                  <p class="text-xs text-hui-foreground/50">{{ n.time }}</p>
                </div>
              </li>
            </ul>
            <NuxtLink
              to="/administration/notifications"
              class="block px-3 py-2 text-sm text-center text-hui-primary hover:bg-hui-default-100 border-t border-hui-divider"
              @click="notifOpen = false"
            >
              Все уведомления
            </NuxtLink>
          </div>
        </Transition>
      </div>

      <!-- Тоггл правого aside: десктоп (xl) — свернуть/развернуть колонку; мобайл — открыть drawer -->
      <button v-if="asideAvailable" type="button" :class="[iconBtn, 'hidden xl:inline-flex']" aria-label="Панель" @click="toggleAside">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <line x1="15" y1="4" x2="15" y2="20" stroke-linecap="round" />
        </svg>
      </button>
      <button v-if="asideAvailable" type="button" :class="[iconBtn, 'xl:hidden']" aria-label="Панель" @click="emit('toggleAsideMobile')">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <rect x="3" y="4" width="18" height="16" rx="2" />
          <line x1="15" y1="4" x2="15" y2="20" stroke-linecap="round" />
        </svg>
      </button>

      <!-- Пользователь -->
      <div ref="userRef" class="relative">
        <button
          type="button"
          class="inline-flex items-center justify-center w-9 h-9 rounded-full bg-hui-secondary text-hui-secondary-foreground text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-hui-focus"
          aria-label="Профиль"
          @click="userOpen = !userOpen"
        >
          A
        </button>
        <Transition
          enter-active-class="transition duration-150 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          leave-active-class="transition duration-100 ease-in"
          leave-to-class="opacity-0 -translate-y-1"
        >
          <div
            v-if="userOpen"
            class="absolute right-0 mt-2 w-48 rounded-hui-md border border-hui-divider bg-hui-content1 shadow-lg overflow-hidden z-50 p-1"
          >
            <NuxtLink
              v-if="canSettings"
              to="/administration/settings"
              class="flex items-center gap-2 px-2.5 py-2 text-sm text-hui-foreground rounded-hui-sm hover:bg-hui-default-100"
              @click="userOpen = false"
            >
              <Icon name="heroicons:cog-6-tooth" class="w-4 h-4" /> Настройки
            </NuxtLink>
            <button
              type="button"
              class="w-full flex items-center gap-2 px-2.5 py-2 text-sm text-hui-danger rounded-hui-sm hover:bg-hui-danger/10"
              @click="logout"
            >
              <Icon name="heroicons:arrow-right-on-rectangle" class="w-4 h-4" /> Выйти
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </header>
</template>
